import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Badge, Btn } from "./common";
import { useI18n } from "@/lib/i18n";

/* Per-project work view (UPDATE_EXPANSION §8) inside the EXISTING Projects screen — no second CRM.
   Notes → timeline (suggestions applied only on click), scope/approvals (owner records who/when/how),
   grounded memory with clickable sources, photo-assisted proposal DRAFT (never sent, never binding). */
const SCOPE_COLOR = { approved: "green", requested: "amber", inferred: "gray", draft: "gray" };
const APPROVAL_SOURCES = ["in_person", "phone", "email", "signed_document", "proposal_accepted", "message"];
// codes → Turkish source labels (translated via tx in EN mode)
const LBL = { draft: "taslak", inferred: "çıkarım", requested: "talep edildi", approved: "onaylandı",
  in_person: "yüz yüze", phone: "telefon", email: "e-posta", signed_document: "imzalı belge",
  proposal_accepted: "teklif kabulü", message: "mesaj", note: "not", task: "görev", proposal: "teklif",
  invoice: "fatura", report: "rapor", scope: "kapsam", mail: "e-posta" };
const L = (code) => LBL[code] || code;
const openRecord = (kind, id, screen) =>
  window.dispatchEvent(new CustomEvent("jarvis-actions", { detail: [{ type: "open_record", kind, id, screen }] }));

const Notes = ({ project, onChanged }) => {
  const { tx } = useI18n();
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState("");
  const [last, setLast] = useState(null);          // suggestions of the note just saved
  const [pick, setPick] = useState({ create: [], complete: [] });
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get(`/projects/${project.project_id}/notes`).then((r) => setNotes(r.data)).catch(() => {}), [project.project_id]);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    try {
      const r = await api.post("/site-notes", { text, project_id: project.project_id, source: "text" });
      setText(""); setLast(r.data); setPick({ create: r.data.suggested_new_tasks || [], complete: (r.data.suggested_complete_tasks || []).map((t) => t.task_id) });
      load();
    } catch (e) { toast.error(tx(e?.response?.data?.detail || "Kaydedilemedi")); }
  };
  const apply = async () => {
    try {
      const r = await api.post(`/site-notes/${last.note.note_id}/apply`, { create_tasks: pick.create, complete_task_ids: pick.complete });
      toast.success(`${tx("Uygulandı")}: +${r.data.tasks_created.length} / ✓${r.data.tasks_completed.length}`);
      setLast(null); onChanged?.();
    } catch (e) { toast.error(tx(e?.response?.data?.detail || "İşlem başarısız")); }
  };
  const correct = async () => {
    try { await api.put(`/site-notes/${edit.note_id}`, { text: edit.text }); setEdit(null); load(); toast.success(tx("Not düzeltildi")); }
    catch (e) { toast.error(tx(e?.response?.data?.detail || "Kaydedilemedi")); }
  };
  const toggle = (k, v) => setPick((p) => ({ ...p, [k]: p[k].includes(v) ? p[k].filter((x) => x !== v) : [...p[k], v] }));
  return (
    <div className="space-y-2" data-testid="project-notes">
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder={tx("Örn: Banyo fayansı bitti, derz kaldı, 2 torba yapıştırıcı lazım")}
        aria-label={tx("Şantiye notu")} className="w-full bg-black/30 border border-cyan-500/20 rounded-lg px-3 py-2 text-xs text-white" data-testid="site-note-input" />
      <Btn onClick={save} disabled={text.trim().length < 3} data-testid="site-note-save">{tx("Notu kaydet")}</Btn>
      {last?.saved && (pick.create.length > 0 || (last.suggested_complete_tasks || []).length > 0 || (last.suggested_new_tasks || []).length > 0) && (
        <div className="bg-black/25 border border-cyan-400/20 rounded-lg p-2 text-xs space-y-1" data-testid="site-note-suggestions">
          <div className="text-white/60">{tx("Önerilenler (yalnızca onayınızla uygulanır):")}</div>
          {(last.suggested_new_tasks || []).map((s) => (
            <label key={s} className="flex gap-2 text-white/80"><input type="checkbox" checked={pick.create.includes(s)} onChange={() => toggle("create", s)} />+ {s}</label>))}
          {(last.suggested_complete_tasks || []).map((t) => (
            <label key={t.task_id} className="flex gap-2 text-white/80"><input type="checkbox" checked={pick.complete.includes(t.task_id)} onChange={() => toggle("complete", t.task_id)} />✓ {t.title}</label>))}
          <Btn onClick={apply} data-testid="site-note-apply">{tx("Seçilenleri uygula")}</Btn>
        </div>
      )}
      {notes.map((n) => (
        <div key={n.note_id} className="text-xs border-l-2 border-cyan-400/30 pl-2" data-testid="site-note-row">
          <div className="text-white/40">{new Date(n.updated_at || n.created_at).toLocaleString()} · {tx(n.source === "voice" ? "ses" : n.source === "assistant" ? "asistan" : "yazı")}{n.revisions?.length ? ` · ${tx("düzeltildi")} (${n.revisions.length})` : ""}</div>
          {edit?.note_id === n.note_id ? (
            <div className="flex gap-1.5 mt-1"><input value={edit.text} onChange={(e) => setEdit({ ...edit, text: e.target.value })} className="flex-1 bg-black/30 border border-white/10 rounded px-2 py-1 text-white" />
              <Btn onClick={correct}>{tx("Kaydet")}</Btn><Btn variant="ghost" onClick={() => setEdit(null)}>{tx("İptal")}</Btn></div>
          ) : (
            <div className="text-white/85">{n.text} <button onClick={() => setEdit({ note_id: n.note_id, text: n.text })} className="text-cyan-300/70 ml-1">{tx("düzelt")}</button></div>
          )}
        </div>
      ))}
    </div>
  );
};

const Scope = ({ project }) => {
  const { tx } = useI18n();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ description: "", status: "draft", approver: "", date: "", source_type: "in_person", source_ref: "" });
  const [check, setCheck] = useState("");
  const [result, setResult] = useState(null);
  const load = useCallback(() => api.get(`/projects/${project.project_id}/scope`).then((r) => setItems(r.data)).catch(() => {}), [project.project_id]);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    const body = { description: form.description, status: form.status };
    if (form.status === "approved") body.approval = { approver: form.approver, date: form.date, source_type: form.source_type, source_ref: form.source_ref };
    try { await api.post(`/projects/${project.project_id}/scope`, body); setForm({ ...form, description: "" }); load(); }
    catch (e) { toast.error(tx(e?.response?.data?.detail || "Kaydedilemedi")); }
  };
  const runCheck = async () => {
    try { const r = await api.post(`/projects/${project.project_id}/scope/check`, { request_text: check }); setResult(r.data); load(); }
    catch (e) { toast.error(tx(e?.response?.data?.detail || "İşlem başarısız")); }
  };
  return (
    <div className="space-y-2 text-xs" data-testid="project-scope">
      {items.map((s) => (
        <div key={s.item_id} className="flex items-start gap-2" data-testid="scope-row">
          <Badge color={SCOPE_COLOR[s.status]}>{tx(L(s.status))}</Badge>
          <div className="text-white/85">{s.description}
            {s.approval && <div className="text-white/45">{tx("Onaylayan")}: {s.approval.approver} · {s.approval.date} · {tx(L(s.approval.source_type))}</div>}
          </div>
        </div>
      ))}
      <div className="flex gap-1.5 flex-wrap items-center">
        <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder={tx("Kapsam kalemi")} aria-label={tx("Kapsam kalemi")}
          className="flex-1 min-w-[160px] bg-black/30 border border-white/10 rounded px-2 py-1 text-white" data-testid="scope-desc" />
        <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} aria-label={tx("Durum")} className="bg-black/30 border border-white/10 rounded px-2 py-1 text-white" data-testid="scope-status">
          {["draft", "requested", "approved"].map((s) => <option key={s} value={s} className="bg-[#0C1222]">{tx(L(s))}</option>)}
        </select>
        {form.status === "approved" && (<>
          <input value={form.approver} onChange={(e) => setForm({ ...form, approver: e.target.value })} placeholder={tx("Onaylayan kişi")} className="bg-black/30 border border-white/10 rounded px-2 py-1 text-white w-28" />
          <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} aria-label={tx("Onay tarihi")} className="bg-black/30 border border-white/10 rounded px-2 py-1 text-white" />
          <select value={form.source_type} onChange={(e) => setForm({ ...form, source_type: e.target.value })} aria-label={tx("Onay kaynağı")} className="bg-black/30 border border-white/10 rounded px-2 py-1 text-white">
            {APPROVAL_SOURCES.map((s) => <option key={s} value={s} className="bg-[#0C1222]">{tx(L(s))}</option>)}
          </select>
        </>)}
        <Btn onClick={add} disabled={!form.description.trim()} data-testid="scope-add">{tx("Ekle")}</Btn>
      </div>
      <div className="flex gap-1.5 pt-1 border-t border-white/10">
        <input value={check} onChange={(e) => setCheck(e.target.value)} placeholder={tx("Müşterinin yeni isteği…")} aria-label={tx("Yeni istek")}
          className="flex-1 bg-black/30 border border-white/10 rounded px-2 py-1 text-white" data-testid="scope-check-input" />
        <Btn variant="ghost" onClick={runCheck} disabled={!check.trim()} data-testid="scope-check">{tx("Kapsamda mı?")}</Btn>
      </div>
      {result && (
        <div className={`rounded p-2 ${result.in_scope ? "bg-emerald-500/10 text-emerald-200" : "bg-amber-500/10 text-amber-200"}`} data-testid="scope-check-result">
          {result.in_scope ? tx("Onaylı kapsamda.") : tx("Onaylı kapsamda değil — 'talep edildi' olarak kaydedildi ve fiyat/onay taslağı hazırlandı (gönderilmedi).")}
          {result.variation_draft && <button className="ml-2 underline" onClick={() => openRecord("proposals", result.variation_draft.proposal_id, "proposals")}>{tx("Taslağı aç")}</button>}
        </div>
      )}
    </div>
  );
};

const Memory = ({ project }) => {
  const { tx } = useI18n();
  const [m, setM] = useState(null);
  useEffect(() => { api.get(`/projects/${project.project_id}/memory`).then((r) => setM(r.data)).catch(() => setM({ error: true })); }, [project.project_id]);
  if (!m) return <div className="text-xs text-white/40">{tx("Yükleniyor…")}</div>;
  if (m.error) return <div className="text-xs text-red-300">{tx("Proje hafızası alınamadı.")}</div>;
  const KIND = { task: "tasks", proposal: "proposals", invoice: "proposals", report: "proposals" };
  return (
    <div className="space-y-2 text-xs" data-testid="project-memory">
      <div className="text-white/85">{m.summary}</div>
      {m.contradictions.map((c, i) => <div key={i} className="text-amber-200 bg-amber-500/10 rounded px-2 py-1" data-testid="memory-contradiction">⚠ {c.note}</div>)}
      <div className="space-y-0.5 max-h-48 overflow-auto scrollbar-thin">
        {m.sources.map((s) => (
          <div key={`${s.type}-${s.id}`} className="flex gap-2 text-white/70">
            <span className="text-white/35 shrink-0">{(s.date || "").slice(0, 10)}</span>
            <span className="text-cyan-300/70 shrink-0">{tx(L(s.type))}</span>
            {KIND[s.type] ? <button className="truncate text-left hover:text-cyan-200 underline decoration-dotted" onClick={() => openRecord(KIND[s.type], s.id, KIND[s.type])}>{s.label}</button>
              : <span className="truncate">{s.label}</span>}
            {s.version === "superseded" && <span className="text-white/35">({tx("eski sürüm")})</span>}
          </div>
        ))}
      </div>
      <div className="text-white/40">{tx(m.mail)}</div>
    </div>
  );
};

const PhotoDraft = ({ project }) => {
  const { tx } = useI18n();
  const [files, setFiles] = useState([]);
  const [sel, setSel] = useState([]);
  const [dims, setDims] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  useEffect(() => { api.get("/files").then((r) => setFiles((r.data || []).filter((f) => f.project_id === project.project_id && (f.content_type || "").startsWith("image/")))).catch(() => {}); }, [project.project_id]);
  const run = async () => {
    setBusy(true);
    const dimensions = Object.fromEntries(dims.split("\n").map((l) => l.split(":")).filter((p) => p.length >= 2 && p[1].trim()).map(([k, ...v]) => [k.trim(), v.join(":").trim()]));
    try { const r = await api.post("/proposals/photo-draft", { project_id: project.project_id, file_ids: sel, dimensions }); setRes(r.data); }
    catch (e) { toast.error(tx(e?.response?.data?.detail || "İşlem başarısız")); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-2 text-xs" data-testid="photo-draft">
      {files.length === 0 ? <div className="text-white/45">{tx("Bu projeye yüklenmiş fotoğraf yok (Dosyalar ekranından yükleyin).")}</div> : (
        <div className="flex gap-2 flex-wrap">
          {files.map((f) => (
            <label key={f.file_id} className="flex items-center gap-1 text-white/75">
              <input type="checkbox" checked={sel.includes(f.file_id)} disabled={!sel.includes(f.file_id) && sel.length >= 4}
                onChange={() => setSel((s) => (s.includes(f.file_id) ? s.filter((x) => x !== f.file_id) : [...s, f.file_id]))} />{f.filename}
            </label>))}
        </div>
      )}
      <textarea value={dims} onChange={(e) => setDims(e.target.value)} rows={2} placeholder={tx("Ölçüler (satır başına): Duvar A: 3.2 x 2.4 m")} aria-label={tx("Ölçüler")}
        className="w-full bg-black/30 border border-white/10 rounded px-2 py-1 text-white" />
      <Btn onClick={run} disabled={!sel.length || busy} data-testid="photo-draft-run">{busy ? tx("Hazırlanıyor…") : tx("Teklif taslağı hazırla")}</Btn>
      <div className="text-white/40">{tx("Yalnızca görsel destekli kaynakta (kendi OpenAI/Claude API) çalışır. Ölçüsü verilmeyen kalemler işaretlenir, uydurulmaz. Taslak bağlayıcı değildir ve gönderilmez.")}</div>
      {res && (
        <div className="bg-black/25 rounded p-2" data-testid="photo-draft-result">
          <button className="underline text-cyan-200" onClick={() => openRecord("proposals", res.proposal.proposal_id, "proposals")}>{res.proposal.number} · {tx("taslağı aç")}</button>
          {res.flagged_measurements?.length > 0 && <div className="text-amber-200 mt-1">{tx("Ölçü gerekli")}: {res.flagged_measurements.join(", ")}</div>}
        </div>
      )}
    </div>
  );
};

export const ProjectWork = ({ project, onChanged }) => {
  const { tx } = useI18n();
  const [tab, setTab] = useState("notes");
  const TABS = [["notes", tx("Notlar")], ["scope", tx("Kapsam")], ["memory", tx("Hafıza")], ["photo", tx("Fotoğraftan taslak")]];
  return (
    <div className="mt-3 border-t border-white/10 pt-2" data-testid="project-work">
      <div className="flex gap-1 mb-2 flex-wrap" role="tablist">
        {TABS.map(([id, label]) => (
          <button key={id} role="tab" aria-selected={tab === id} onClick={() => setTab(id)}
            className={`px-2.5 py-1 rounded-full text-[0.7rem] border ${tab === id ? "border-cyan-400/50 bg-cyan-500/15 text-cyan-200" : "border-white/10 text-white/55"}`}>{label}</button>
        ))}
      </div>
      {tab === "notes" && <Notes project={project} onChanged={onChanged} />}
      {tab === "scope" && <Scope project={project} />}
      {tab === "memory" && <Memory project={project} />}
      {tab === "photo" && <PhotoDraft project={project} />}
    </div>
  );
};

export default ProjectWork;
